import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DataTable } from "@/components/ui";
import { DigestSubscription, DigestSubscriptionServices, DigestHistory } from "@/services/bedrock";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment-timezone";

interface DigestHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: DigestSubscription;
}

export const DigestHistoryDialog: React.FC<DigestHistoryDialogProps> = ({ open, onOpenChange, subscription }) => {
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  const { data, isLoading } = useQuery({
    queryKey: ["digestHistory", subscription.digestSubscriptionId, pagination],
    queryFn: () => DigestSubscriptionServices.getHistory(subscription.digestSubscriptionId, pagination),
    enabled: open,
  });

  const columns: ColumnDef<DigestHistory>[] = [
    {
      accessorKey: "createdAt",
      header: "Sent At",
      cell: ({ getValue }) => {
        const val = getValue() as string;
        if (!val) return "-";
        try {
          return moment(val).tz(moment.tz.guess()).format("MMM D, YYYY h:mm a z");
        } catch {
          return val;
        }
      },
    },
    {
      accessorKey: "recipientName",
      header: "Recipient",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-slate-700">{row.original.recipientName}</span>
          <span className="text-xs text-slate-500">{row.original.recipientEmail}</span>
        </div>
      ),
    },
    {
      accessorKey: "readAt",
      header: "Status",
      cell: ({ row }) => {
        if (row.original.readAt)
          return <span className="text-emerald-600 font-bold text-xs uppercase tracking-wider">Read</span>;
        if (row.original.seenAt)
          return <span className="text-blue-600 font-bold text-xs uppercase tracking-wider">Delivered</span>;
        return <span className="text-slate-400 font-medium text-xs uppercase tracking-wider">Sent</span>;
      },
      meta: { intrinsic: true },
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={"xl"}>
      <Dialog.Header>
        <Dialog.Title>Subscription History</Dialog.Title>
        <Dialog.Description>
          Viewing sent subscription history for your{" "}
          <span className="capitalize font-semibold text-slate-900">{subscription.frequency}</span> subscription.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <div className="py-6">
          <div className="rounded-xl border border-slate-200 overflow-hidden shadow-xs">
            <DataTable
              columns={columns}
              data={data?.content || []}
              loading={isLoading}
              pagination={pagination}
              onPaginationChange={setPagination}
              pageCount={data?.pagination?.totalPages}
              rowCount={data?.pagination?.totalCount}
              emptyState={
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-slate-500 italic">No history found for this subscription.</p>
                </div>
              }
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
